import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import PackageFormDrawer, { MODEL_CATEGORY_OPTIONS } from "@/components/PackageFormDrawer";
import PackageHistoryDrawer from "@/components/PackageHistoryDrawer";
import PackageCsvIO from "@/components/PackageCsvIO";
import {
  Plus,
  PencilSimple,
  Trash,
  Package as PackageIcon,
  CheckCircle,
  XCircle,
  ClockClockwise,
  Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import useBranchTypes from "@/hooks/useBranchTypes";

function PackageRow({ pkg, branchTypeName, isAdmin, onEdit, onDelete, onHistory }) {
  return (
    <tr
      data-testid={`package-row-${pkg.id}`}
      className="border-b border-canvas-line hover:bg-canvas-panel transition-colors"
    >
      <td className="px-4 py-3">
        <div className="text-sm font-semibold text-ink">{pkg.name}</div>
        {pkg.description && (
          <div className="text-[12px] text-ink-muted mt-0.5 line-clamp-1 max-w-md">
            {pkg.description}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {pkg.model_category ? (
          <span className="inline-flex items-center px-2 py-0.5 border border-canvas-line text-[10px] font-mono uppercase tracking-wider">
            {pkg.model_category}
          </span>
        ) : (
          <span className="font-mono text-[10px] text-ink-subtle">—</span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{branchTypeName}</td>
      <td className="px-4 py-3 font-mono text-sm font-semibold text-ink">{fmtMoney(pkg.price)}</td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">
        {pkg.items?.length || 0} item{(pkg.items?.length || 0) === 1 ? "" : "s"}
      </td>
      <td className="px-4 py-3">
        {pkg.active ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-[#00B341]">
            <CheckCircle size={12} weight="fill" /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-ink-subtle">
            <XCircle size={12} weight="fill" /> Inactive
          </span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{fmtDate(pkg.created_at)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={() => onHistory(pkg)}
            data-testid={`history-package-${pkg.id}`}
            className="p-1.5 border border-canvas-line hover:border-ink transition-colors"
            aria-label="View price history"
            title="Price history"
          >
            <ClockClockwise size={13} weight="bold" />
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => onEdit(pkg)}
                data-testid={`edit-package-${pkg.id}`}
                className="p-1.5 border border-canvas-line hover:border-ink"
              >
                <PencilSimple size={13} weight="bold" />
              </button>
              <button
                onClick={() => onDelete(pkg)}
                data-testid={`delete-package-${pkg.id}`}
                className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive"
              >
                <Trash size={13} weight="bold" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function PackagesPage() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyPkg, setHistoryPkg] = useState(null);
  const [branchTypeFilter, setBranchTypeFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const { types: branchTypes, byId: branchTypeById } = useBranchTypes();
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = branchTypeFilter ? { branch_type_id: branchTypeFilter } : {};
      const { data } = await api.get("/packages", { params });
      setPackages(data);
    } finally {
      setLoading(false);
    }
  }, [branchTypeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setShowForm(true);
  };
  const handleSaved = () => {
    setShowForm(false);
    load();
  };
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete package "${p.name}"? It will be removed from any vehicles using it.`)) return;
    try {
      await api.delete(`/packages/${p.id}`);
      toast.success("Package deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // One-click admin backfill: walks every historical sale/booking/vehicle and
  // stamps the primary package's category onto the top-level + each snapshot
  // packages[i].model_category. Idempotent — rows already populated are
  // skipped server-side.
  const runBackfill = async () => {
    const ok = window.confirm(
      "Walk every sale, booking, and vehicle and stamp `model_category` from the primary package?\n\n" +
        "This is safe to re-run — only rows missing the category get updated.",
    );
    if (!ok) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/packages/admin/backfill-model-category");
      if (data.skipped_no_categorised_packages) {
        toast.info("No packages have a model_category yet — categorise at least one package first.");
      } else {
        toast.success(
          `Backfill complete · vehicles: ${data.vehicles}, sales: ${data.sales}, bookings: ${data.bookings}`,
        );
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="04 / Packages"
        title="Sales Packages"
        subtitle="Reusable bundles — warranties, services, add-ons. Attach them to vehicles and toggle at sale time."
        actions={
          <div className="flex items-center gap-2">
            <PackageCsvIO onImported={load} isAdmin={isAdmin} />
            {isAdmin && (
              <button
                onClick={runBackfill}
                disabled={backfilling}
                data-testid="backfill-model-category"
                title="Walk every sale, booking, and vehicle and stamp model_category from the primary package. Safe to re-run."
                className="border border-canvas-line px-3 py-2.5 text-sm font-mono uppercase tracking-wider text-ink hover:bg-canvas-panel disabled:opacity-50 flex items-center gap-2"
              >
                <Lightning size={13} weight="bold" />
                {backfilling ? "Backfilling…" : "Backfill Model"}
              </button>
            )}
            {isAdmin && (
              <button
                onClick={openCreate}
                data-testid="add-package-button"
                className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
              >
                <Plus size={14} weight="bold" />
                Create Package
              </button>
            )}
          </div>
        }
      />

      <div className="px-8 py-6">
        <div className="mb-4 flex items-center justify-end gap-4">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              Model:
            </span>
            <select
              data-testid="package-model-filter"
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">All models</option>
              {MODEL_CATEGORY_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              Branch type:
            </span>
            <select
              data-testid="package-branch-type-filter"
              value={branchTypeFilter}
              onChange={(e) => setBranchTypeFilter(e.target.value)}
              className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">All types</option>
              {branchTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <table className="w-full" data-testid="packages-table">
            <thead>
              <tr className="border-b border-canvas-line bg-canvas-panel">
                {["Package", "Model", "Branch Type", "Price", "Items", "Status", "Created", ""].map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && packages.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <PackageIcon size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                    <div className="font-mono text-xs text-ink-subtle mb-3">No packages yet.</div>
                    {isAdmin && (
                      <button
                        onClick={openCreate}
                        className="text-sm font-semibold text-brand hover:underline"
                      >
                        + Create your first package
                      </button>
                    )}
                  </td>
                </tr>
              )}
              {packages
                .filter((p) =>
                  modelFilter ? (p.model_category || "").toUpperCase() === modelFilter : true,
                )
                .map((p) => (
                  <PackageRow
                    key={p.id}
                    pkg={p}
                    branchTypeName={branchTypeById[p.branch_type_id]?.name || "—"}
                    isAdmin={isAdmin}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                    onHistory={setHistoryPkg}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {isAdmin && (
        <PackageFormDrawer
          open={showForm}
          editing={editing}
          onClose={() => setShowForm(false)}
          onSaved={handleSaved}
          defaultBranchTypeId={branchTypeFilter || "general"}
        />
      )}
      <PackageHistoryDrawer
        open={!!historyPkg}
        pkg={historyPkg}
        onClose={() => setHistoryPkg(null)}
      />
    </div>
  );
}
